import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Bell, ShoppingCart, CreditCard, Truck, Package, AlertTriangle,
  Crown, GraduationCap, Heart, Share2, Clock, CheckCircle2, Trash2,
  CheckCheck, ChevronLeft, ChevronRight, X
} from 'lucide-react';

const typeIcons = {
  order_created: ShoppingCart,
  payment_success: CreditCard,
  payment_failed: X,
  order_shipped: Truck,
  order_delivered: Package,
  low_stock: AlertTriangle,
  membership_active: Crown,
  course_enrolled: GraduationCap,
  donation_received: Heart,
  affiliate_commission: Share2,
  termin_reminder: Clock,
  termin_overdue: AlertTriangle,
  cod_confirmed: Truck,
  order_canceled: X,
};

const typeColors = {
  order_created: 'bg-blue-100 text-blue-600',
  payment_success: 'bg-green-100 text-green-600',
  payment_failed: 'bg-red-100 text-red-600',
  order_shipped: 'bg-indigo-100 text-indigo-600',
  order_delivered: 'bg-emerald-100 text-emerald-600',
  low_stock: 'bg-amber-100 text-amber-600',
  membership_active: 'bg-purple-100 text-purple-600',
  course_enrolled: 'bg-cyan-100 text-cyan-600',
  donation_received: 'bg-pink-100 text-pink-600',
  affiliate_commission: 'bg-orange-100 text-orange-600',
  termin_reminder: 'bg-yellow-100 text-yellow-600',
  termin_overdue: 'bg-red-100 text-red-600',
  cod_confirmed: 'bg-teal-100 text-teal-600',
  order_canceled: 'bg-gray-100 text-gray-600',
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Baru saja';
  if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} hari lalu`;
  return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [loading, setLoading] = useState(true);
  const limit = 15;

  document.title = 'Pusat Notifikasi — Tokomoe';

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filter === 'unread') params.is_read = 'false';
      if (filter === 'read') params.is_read = 'true';
      const { data } = await api.get('/tenant/in-app-notifications', { params });
      setNotifications(data.notifications || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    await api.put(`/tenant/in-app-notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const markAllRead = async () => {
    await api.put('/tenant/in-app-notifications/mark-all-read');
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const deleteNotif = async (id) => {
    await api.delete(`/tenant/in-app-notifications/${id}`);
    setNotifications(prev => prev.filter(n => n.id !== id));
    setTotal(prev => prev - 1);
  };

  const totalPages = Math.ceil(total / limit);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pusat Notifikasi</h1>
          <p className="text-sm text-slate-500 mt-1">Semua notifikasi aktivitas toko Anda</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead} className="gap-2">
            <CheckCheck size={16} /> Tandai Semua Dibaca
          </Button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'all', label: 'Semua' },
          { key: 'unread', label: 'Belum Dibaca' },
          { key: 'read', label: 'Sudah Dibaca' },
        ].map(f => (
          <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm"
            onClick={() => { setFilter(f.key); setPage(1); }}>
            {f.label}
          </Button>
        ))}
      </div>

      {/* Notification List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Bell size={48} className="text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">Tidak ada notifikasi</p>
            <p className="text-slate-400 text-sm mt-1">
              {filter === 'unread' ? 'Semua notifikasi sudah dibaca' : 'Belum ada notifikasi masuk'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map(notif => {
            const Icon = typeIcons[notif.type] || Bell;
            const colorClass = typeColors[notif.type] || 'bg-slate-100 text-slate-600';
            return (
              <Card key={notif.id}
                className={`transition-all hover:shadow-sm ${!notif.is_read ? 'border-l-4 border-l-[#2D6CDF] bg-blue-50/30' : ''}`}>
                <CardContent className="flex items-start gap-4 py-4 px-5">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-sm font-semibold ${!notif.is_read ? 'text-slate-900' : 'text-slate-600'}`}>
                        {notif.title}
                      </h3>
                      {!notif.is_read && (
                        <span className="w-2 h-2 rounded-full bg-[#2D6CDF] shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!notif.is_read && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-green-600"
                        onClick={() => markRead(notif.id)} title="Tandai dibaca">
                        <CheckCircle2 size={16} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600"
                      onClick={() => deleteNotif(notif.id)} title="Hapus">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-500">
            Menampilkan {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} dari {total} notifikasi
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={16} />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
